import { Fragment, useEffect, useState, useMemo } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Tippy from '@tippyjs/react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';

import IconX from '../Icon/IconX';
import IconInfoCircle from '../Icon/IconInfoCircle';
import TaxStatusSelect from '../TaxStatusSelect';
import { analytics } from '../../utils/analytics';
import { metricsService, MetricEvent } from '../../services/metricsService';

import { selectSettings } from '../../store/authSlice';
import { CALCULATION_CURRENCY } from '../../utils/currencyUtils';
import { useSmartRules } from '../../contexts/SmartRulesContext';
import {
    TransactionType,
    OptionalTransactionType,
    Distribution,
    LedgerTransaction, // Updated
    Account,
    AccountType,
    SelectOption,
    AllTransactionType
} from '../../global/types';
import { getCategoryKey } from '../../utils/translation';
import CurrencyInputRight from '../CurrencyInputRight';

export type TransactionFormInput = {
    date: string;
    amount: number | null;
    subCategoryId: string;
    category: TransactionType | OptionalTransactionType;
    accountProvenanceId: string | null;
    accountReceivableId: string | null;
    distribution: Distribution | null;
    spenderOrEarnerId?: string;
    description?: string;
    location?: string | null;
    isBeforeTax?: boolean | null;
    taxRate?: number | null;
    currency?: string;
};

export type ValidatedTransactionFormInput = TransactionFormInput & { amount: number };

export const categoryOptions = [
    { value: TransactionType.INCOME, label: 'Income' },
    { value: TransactionType.BILLS, label: 'Bills' },
    { value: TransactionType.SUBSCRIPTIONS, label: 'Subscriptions' },
    { value: TransactionType.DEBTS, label: 'Debts' },
    { value: OptionalTransactionType.EXPENSES, label: 'Expenses' },
    { value: OptionalTransactionType.SAVINGS, label: 'Savings' },
];

interface TransactionFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    transaction: LedgerTransaction | null;
    options?: Array<Array<{ value: string; label: string; category?: string }>>;
    onSubmit: (data: ValidatedTransactionFormInput, isUpdate: boolean, keepOpen?: boolean) => void | Promise<void>;
    accounts: Account[];
    forbiddenSubCategoryIds: string[] | null;
    baseCurrency?: string;
}

const buildDefaultFormValues = (
    transaction: LedgerTransaction | null,
    defaultTaxRate: number | undefined,
    baseCurrency: string | undefined
): TransactionFormInput => {
    if (!transaction) {
        return {
            date: dayjs().format('YYYY-MM-DD'),
            amount: null,
            subCategoryId: '',
            category: OptionalTransactionType.EXPENSES,
            accountProvenanceId: null,
            accountReceivableId: null,
            distribution: null,
            spenderOrEarnerId: '',
            description: '',
            location: '',
            isBeforeTax: false,
            taxRate: defaultTaxRate,
            currency: baseCurrency || CALCULATION_CURRENCY,
        };
    }

    const isIncome = transaction.type === TransactionType.INCOME;
    return {
        date: dayjs(transaction.date.toDate()).format('YYYY-MM-DD'),
        amount: transaction.amount,
        subCategoryId: transaction.categoryId || '',
        category: transaction.categoryGroup as any, // categoryGroup is broader, might need careful mapping if we use strict types
        // Map generic accountId to specific UI fields based on type
        accountProvenanceId: !isIncome ? transaction.accountId : null,
        accountReceivableId: isIncome ? transaction.accountId : (transaction.transferAccountId || null),
        distribution: transaction.distribution || null,
        spenderOrEarnerId: transaction.spenderOrEarnerId || '',
        description: transaction.description || '',
        location: transaction.location || '',
        isBeforeTax: transaction.isBeforeTax || false,
        taxRate: transaction.taxRate || 0,
        currency: transaction.currency || baseCurrency || CALCULATION_CURRENCY,
    };
};

const TransactionFormModal = ({
    isOpen,
    onClose,
    transaction,
    options,
    onSubmit,
    accounts,
    forbiddenSubCategoryIds,
    baseCurrency,
}: TransactionFormModalProps) => {
    const { t } = useTranslation();
    const isUpdateMode = !!transaction;
    const settings = useSelector(selectSettings);
    const { control, register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm<TransactionFormInput>({
        defaultValues: buildDefaultFormValues(transaction, settings?.defaultTaxRate, baseCurrency),
    });
    const { findMatchingRule } = useSmartRules();

    // Watch description for smart categorization
    const description = watch('description');

    useEffect(() => {
        if (!isUpdateMode && description && !transaction) {
            const rule = findMatchingRule(description);
            if (rule) {
                setValue('subCategoryId', rule.categoryId);
                setValue('category', rule.categoryType);
            }
        }
    }, [description, isUpdateMode, transaction, findMatchingRule, setValue]);

    const [
        subCategoriesOptions = [],
        rawAccountsOptions = [],
        spendersOptions = [],
    ] = options || [[], [], []];

    // Filter out investment and crypto accounts as they are auto-synced
    const accountsOptions = useMemo(() => {
        return rawAccountsOptions.filter(option =>
            option.category !== AccountType.INVESTMENT &&
            option.category !== AccountType.CRYPTO
        );
    }, [rawAccountsOptions]);

    const [filteredSubCategoriesOptions, setFilteredSubCategoriesOptions] = useState<SelectOption[]>([]);
    const [keepOpen, setKeepOpen] = useState(false);
    const isIncomeType = watch('category') === TransactionType.INCOME;

    const selectedAccountId = isIncomeType ? watch('accountReceivableId') : watch('accountProvenanceId');
    const selectedAccountCurrency = accounts.find(account => account.id === selectedAccountId)?.currency;

    useEffect(() => {
        if (selectedAccountCurrency) setValue('currency', selectedAccountCurrency, { shouldValidate: false });
    }, [selectedAccountCurrency, setValue]);
    const hideSubCategory = filteredSubCategoriesOptions.length === 0;

    const onSubmitLocal = async (data: any) => {
        if (isIncomeType) {
            data.accountProvenanceId = null;
        } else {
            data.accountReceivableId = null;
            data.isBeforeTax = null;
            data.taxRate = null;
        }
        data.distribution = data.distribution || null;
        data.subCategoryId = data.subCategoryId || null;

        if (typeof data.amount !== 'number' || !Number.isFinite(data.amount)) {
            return;
        }

        try {
            await onSubmit({ ...data, amount: data.amount }, isUpdateMode, keepOpen);

            if (!isUpdateMode) {
                analytics.track('transaction_created', {
                    transaction_type: isIncomeType ? 'income' : 'expense',
                    category: data.category,
                    source: 'manual',
                    has_account: Boolean(data.accountProvenanceId || data.accountReceivableId),
                });

                analytics.track('expense_logged', {
                    category: data.category || 'unknown',
                    amount: data.amount,
                    source: 'manual',
                });
                // legacy helper kept for backward compatibility
                // trackTransactionManualAdd replaced by analytics

                // Track Sentry metrics
                metricsService.trackEvent(MetricEvent.TRANSACTION_CREATED, {
                    type: isIncomeType ? 'income' : 'expense',
                    category: data.category,
                    source: 'manual'
                });
            } else {
                analytics.track('transaction_updated', {
                    transaction_type: isIncomeType ? 'income' : 'expense',
                    category: data.category,
                });

                // Track transaction edit
                metricsService.trackEvent(MetricEvent.TRANSACTION_EDITED, {
                    type: isIncomeType ? 'income' : 'expense',
                    category: data.category
                });
            }

            // Reset form but keep some useful defaults if keeping open
            if (keepOpen) {
                reset({
                    ...data,
                    amount: undefined,
                    description: '',
                    // Keep date, category, account, etc. for faster entry
                });
                // Ensure keepOpen is reset for next time
                setKeepOpen(false);
            } else {
                reset();
            }
        } catch (error) {
            // If submit fails, do not reset the form so user can try again
            console.error("Submission failed, form not reset", error);
            if (!isUpdateMode) {
                analytics.track('transaction_creation_failed', { transaction_type: isIncomeType ? 'income' : 'expense', category: data.category });
            }
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        reset(buildDefaultFormValues(transaction, settings?.defaultTaxRate, baseCurrency));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, transaction?.id, settings?.defaultTaxRate, baseCurrency]);

    useEffect(() => {
        if (!forbiddenSubCategoryIds) return;
        const cat = watch('category');
        const filtered = subCategoriesOptions.filter(
            o => !forbiddenSubCategoryIds.includes(o.value) && o.category === cat
        );
        setFilteredSubCategoriesOptions(filtered);

        if (transaction && transaction.categoryGroup === cat && filtered.some(o => o.value === transaction.categoryId)) {
            setValue('subCategoryId', transaction.categoryId || '', { shouldValidate: false });
        }
    }, [watch('category'), forbiddenSubCategoryIds, subCategoriesOptions, transaction]);

    // Currency is set once on form reset/init (in the previous useEffect).
    // Removed auto-sync with account because it was overriding user's manual currency selection.

    const showDistribution = ![TransactionType.INCOME, OptionalTransactionType.SAVINGS].includes(watch("category") as unknown as AllTransactionType);
    const hideTaxRate = !isIncomeType || !isUpdateMode || transaction?.taxRate === settings?.defaultTaxRate;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" static open={isOpen} onClose={onClose} className="relative z-50">
                <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-[black]/70 backdrop-blur-sm" />
                </TransitionChild>
                <div className="fixed inset-0 z-[999] overflow-y-auto">
                    <div className="flex items-end sm:items-center justify-center min-h-screen sm:px-4 sm:py-8">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="panel border-0 p-0 rounded-none rounded-t-[32px] sm:rounded-[32px] overflow-hidden w-full max-w-2xl max-h-[92dvh] sm:max-h-none flex flex-col text-black dark:text-white-dark shadow-2xl bg-white/95 backdrop-blur-md dark:bg-[#121c2c]/95">
                                {/* Header */}
                                <div className="text-xl font-bold bg-white/50 dark:bg-[#121c2c]/50 px-5 py-4 sm:pl-8 sm:pr-8 sm:py-6 flex justify-between items-center shrink-0">
                                    <span>{isUpdateMode ? t('edit_transaction') : t('add_transaction')}</span>
                                    <button
                                        onClick={onClose}
                                        type="button"
                                        className="text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        <IconX className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Form Content */}
                                <div className="p-5 sm:p-8 overflow-y-auto no-scrollbar flex-1 min-h-0 sm:max-h-[calc(100vh-200px)]">
                                    <form onSubmit={handleSubmit(onSubmitLocal)}>
                                        <div className="space-y-6">
                                            {/* Date & Amount Section */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Date Field */}
                                                <div>
                                                    <label htmlFor="date" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                        {t('date')}
                                                    </label>
                                                    <input
                                                        id="date"
                                                        type="date"
                                                        className="form-input w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                        {...register('date', { required: true })}
                                                    />
                                                </div>

                                                {/* Amount Field */}
                                                <div>
                                                    <label htmlFor="amount" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                        {t('amount')}
                                                    </label>
                                                    <div className="flex h-12">
                                                        <input
                                                            id="amount"
                                                            type="number"
                                                            step="any"
                                                            placeholder="0.00"
                                                            className={`form-input flex-1 min-w-0 h-full ltr:!rounded-r-none ltr:!rounded-l-2xl rtl:!rounded-l-none rtl:!rounded-r-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${errors.amount ? '!border-danger focus:!border-danger focus:!ring-danger/20' : ''}`}
                                                            {...register('amount', {
                                                                required: t('amount_required', { defaultValue: 'Amount is required' }),
                                                                min: { value: 0.01, message: t('amount_min', { defaultValue: 'Amount must be greater than 0' }) },
                                                                valueAsNumber: true
                                                            })}
                                                        />
                                                        {errors.amount && <p className="text-danger text-xs mt-1 absolute -bottom-5 left-0">{errors.amount.message}</p>}
                                                        <CurrencyInputRight
                                                            radius="2xl"
                                                            code={selectedAccountCurrency || '-'}
                                                            title={t('transaction_currency_follows_account', {
                                                                defaultValue: 'Set by the selected account; use a transfer to move between currencies'
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Tax Status Row (Income only) */}
                                            {isIncomeType && (
                                                <div className="flex items-center gap-4 p-3 border border-white-light dark:border-[#1b2e4b] rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Tax Status:
                                                        </label>
                                                        <Controller
                                                            name="isBeforeTax"
                                                            control={control}
                                                            render={({ field: { onChange, value } }) => (
                                                                <TaxStatusSelect
                                                                    isBeforeTax={Boolean(value)}
                                                                    onChange={(value: boolean) => onChange(value)}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    {!hideTaxRate && (
                                                        <div className="flex items-center gap-2 ml-auto">
                                                            <label htmlFor="taxRate" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                                                                {t('tax_rate')}
                                                                <TaxRateTippy />
                                                            </label>
                                                            <div className="flex w-32">
                                                                <input
                                                                    id="taxRate"
                                                                    className="form-input rounded-r-none h-9 text-sm"
                                                                    type="number"
                                                                    step={0.01}
                                                                    placeholder="0.00"
                                                                    {...register("taxRate", { valueAsNumber: true })}
                                                                />
                                                                <div className="bg-[#eee] flex justify-center items-center ltr:rounded-r-md rtl:rounded-l-md px-2 text-sm font-semibold border ltr:border-l-0 rtl:border-r-0 border-white-light dark:border-[#17263c] dark:bg-[#1b2e4b]">
                                                                    %
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Location & Description */}
                                            <div className="md:col-span-2">
                                                <label htmlFor="location" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                    {t('location')}
                                                </label>
                                                <input
                                                    id="location"
                                                    type="text"
                                                    placeholder={t('location_placeholder')}
                                                    className="form-input w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                    {...register('location')}
                                                />
                                            </div>

                                            {/* Divider */}
                                            <div className="border-t border-gray-200 dark:border-gray-700"></div>

                                            {/* Category Section */}
                                            <div className="space-y-4">
                                                <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                                                    {t('categorization')}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label htmlFor="category" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                            {t('category')}
                                                        </label>
                                                        <select
                                                            id="category"
                                                            className="form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                            {...register('category', { required: true })}
                                                        >
                                                            {categoryOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {t(option.label.toLowerCase() as any)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className={hideSubCategory ? 'hidden' : ''}>
                                                        <label htmlFor="subCategoryId" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                            {t("sub_category")}
                                                        </label>
                                                        <select
                                                            id="subCategoryId"
                                                            className={`form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${errors.subCategoryId ? '!border-danger focus:!border-danger focus:!ring-danger/20' : ''}`}
                                                            {...register('subCategoryId', {
                                                                required: false,
                                                                onChange: (e) => {
                                                                    const selectedOption = subCategoriesOptions.find(option => option.value === e.target.value);
                                                                    if (selectedOption) {
                                                                        setValue('subCategoryId', e.target.value, { shouldValidate: true });
                                                                        // Also update category if needed, but ensure it doesn't break the flow
                                                                        if (selectedOption.category) {
                                                                            setValue('category', selectedOption.category as TransactionType | OptionalTransactionType, { shouldValidate: true });
                                                                        }
                                                                    }
                                                                }
                                                            })}
                                                        >
                                                            <option value="">{t('select_sub_category_placeholder')}</option>
                                                            {filteredSubCategoriesOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {t(getCategoryKey(option.label) as any)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors.subCategoryId && <p className="text-danger text-xs mt-1">{errors.subCategoryId.message}</p>}
                                                    </div>
                                                </div>

                                                {/* Distribution (conditional) */}
                                                <AnimateHeight duration={300} height={showDistribution ? 'auto' : 0}>
                                                    <div className="p-5 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                                                        <label htmlFor="distribution" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                            {t('50_30_20_distribution')}
                                                        </label>
                                                        <select
                                                            id="distribution"
                                                            className="form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                            {...register('distribution', { disabled: !showDistribution, required: true })}
                                                        >
                                                            <option value="">{t('select_distribution_placeholder')}</option>
                                                            <option value="NEED">{t('need')}</option>
                                                            <option value="WANT">{t('want')}</option>
                                                            <option value="SAVINGS">{t('saving')} / {t('debt')}</option>
                                                        </select>
                                                    </div>
                                                </AnimateHeight>
                                            </div>

                                            {/* Divider */}
                                            <div className="border-t border-gray-200 dark:border-gray-700"></div>

                                            {/* Account & Person Section */}
                                            <div className="space-y-4">
                                                <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                                                    {t('details')}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {!isIncomeType && (
                                                        <div>
                                                            <label htmlFor="accountProvenanceId" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                                {t('account_provenance')}
                                                            </label>
                                                            <select
                                                                id="accountProvenanceId"
                                                                className={`form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${errors.accountProvenanceId ? '!border-danger focus:!border-danger focus:!ring-danger/20' : ''}`}
                                                                {...register('accountProvenanceId', {
                                                                    required: t('account_required', { defaultValue: 'Account is required' })
                                                                })}
                                                            >
                                                                <option value="">{t('select_account_placeholder')}</option>
                                                                {accountsOptions.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {errors.accountProvenanceId && <p className="text-danger text-xs mt-1">{errors.accountProvenanceId.message}</p>}
                                                        </div>
                                                    )}
                                                    {isIncomeType && (
                                                        <div>
                                                            <label htmlFor="accountReceivableId" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                                {t('account_receivable')}
                                                            </label>
                                                            <select
                                                                id="accountReceivableId"
                                                                className={`form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${errors.accountReceivableId ? '!border-danger focus:!border-danger focus:!ring-danger/20' : ''}`}
                                                                {...register('accountReceivableId', {
                                                                    required: t('account_required', { defaultValue: 'Account is required' })
                                                                })}
                                                            >
                                                                <option value="">{t('select_account_placeholder')}</option>
                                                                {accountsOptions.map((option) => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {errors.accountReceivableId && <p className="text-danger text-xs mt-1">{errors.accountReceivableId.message}</p>}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label htmlFor="spenderOrEarnerId" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                            {t('spender')}/{t('earner')}
                                                        </label>
                                                        <select
                                                            id="spenderOrEarnerId"
                                                            className="form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                            {...register('spenderOrEarnerId')}
                                                        >
                                                            <option value="">{t('select_spender_earner_placeholder')}</option>
                                                            {spendersOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label htmlFor="description" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                        {t('description')} <span className="text-gray-400 font-normal">({t('optional')})</span>
                                                    </label>
                                                    <textarea
                                                        id="description"
                                                        className="form-textarea w-full !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[100px] resize-none"
                                                        rows={3}
                                                        {...register('description')}
                                                        placeholder={t('description_placeholder')}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Actions */}
                                        <div className="flex flex-col sm:flex-row sm:justify-end items-stretch sm:items-center gap-3 sm:gap-4 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700/50">
                                            <button
                                                onClick={onClose}
                                                type="button"
                                                className="btn btn-outline-danger w-full sm:w-auto px-6 h-12 rounded-full font-bold border-2 hover:bg-danger hover:text-white transition-all"
                                            >
                                                {t('cancel')}
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary w-full sm:w-auto px-8 h-12 rounded-full font-bold shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all"
                                            >
                                                {isUpdateMode ? t('update') : t('add')}
                                            </button>
                                            {!isUpdateMode && (
                                                <button
                                                    type="submit"
                                                    className="btn btn-outline-primary w-full sm:w-auto px-8 h-12 rounded-full font-bold border-2 hover:bg-primary hover:text-white transition-all whitespace-nowrap"
                                                    onClick={() => setKeepOpen(true)}
                                                >
                                                    {t('save_and_create_another')}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

function TaxRateTippy() {
    const { t } = useTranslation();
    const settings = useSelector(selectSettings);

    return (
        <Tippy content={t('default_tax_rate_tip', { rate: String(settings?.defaultTaxRate ?? 0) })} placement="top">
            <div><IconInfoCircle className="size-4" /></div>
        </Tippy>
    )
}

export default TransactionFormModal;
